import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Button, CTA, Reveal, SectionIntro, useSeo } from "../components/Shared";
import { SolutionNavigator } from "../components/SolutionNavigator";
import { Faq } from "../components/Faq";
import { useContent } from "../context/ContentContext";
import { ARTICLES, DIVISIONS, IMG, MARQUEE, TESTIMONIALS } from "../data/content";

const Hero = () => {
  const reduce = useReducedMotion();
  const { c } = useContent();
  return (
    <section className="hero" data-testid="home-hero">
      <div className="hero-media">
        <motion.img
          src={IMG.hero} alt="Dishan International Group, premium corporate environment"
          initial={reduce ? false : { scale: 1.08 }}
          animate={reduce ? {} : { scale: 1 }}
          transition={{ duration: 12, ease: "linear" }}
        />
        <div className="hero-overlay" />
      </div>
      <div className="container hero-copy">
        <Reveal>
          <span className="eyebrow on-dark">Dishan International Group · Strategic ecosystem</span>
          <h1 style={{ whiteSpace: "pre-line" }}>{c("home.hero.headline", "Strategy. Growth.\nTransformation.")}</h1>
        </Reveal>
        <Reveal delay={0.12}>
          <p>{c("home.hero.subtext", "Strategic consulting, professional education, brand thinking and human potential solutions, brought together for people and organizations ready to move forward.")}</p>
        </Reveal>
        <Reveal delay={0.22} className="hero-actions">
          <Button>Book a Consultation</Button>
          <Button to="/services" variant="ghost">Explore Our Services</Button>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  useSeo(
    "Dishan International Group | Strategy. Growth. Transformation.",
    "A premium strategic ecosystem spanning business & brand consulting, education, mental health and corporate solutions. Think Positive. Be Realistic."
  );
  const { c } = useContent();
  return (
    <main>
      <Hero />
      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={`${m}-${i}`}>{m}<i className="dot" /></span>
          ))}
        </div>
      </div>
      <section className="section">
        <div className="container">
          <SectionIntro
            eyebrow="One connected point of view"
            title={c("home.divisions.title", "A strategic ecosystem, not a single service.")}
            copy={c("home.divisions.copy", "Dishan brings together the thinking, learning and support required to turn intention into meaningful progress, for a business, a career, a brand or a life.")}
            action={{ label: "Understand Our Approach", to: "/about" }}
          />
          <div className="division-grid">
            {DIVISIONS.map((d, i) => (
              <Reveal key={d.num} delay={i * 0.08}>
                <Link to={d.path} className="division-card" data-testid={`division-${d.num}-card`}>
                  <div className="division-media"><img src={d.img} alt={d.title} loading="lazy" /></div>
                  <div className="division-body">
                    <div className="division-head"><span className="card-number">{d.num}</span><span className="tag">{d.tag}</span></div>
                    <h3>{d.title}</h3>
                    <p>{d.copy}</p>
                    <span className="card-link">Explore Division <ArrowUpRight size={15} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="section navy">
        <div className="container split-section perspective">
          <div>
            <Reveal>
              <span className="eyebrow on-dark">The Dishan perspective</span>
              <h2>{c("home.perspective.title", "Growth is more durable when it is human.")}</h2>
            </Reveal>
            <Reveal delay={0.1} className="large-copy">
              <p>{c("home.perspective.copy", "We believe the most valuable solutions are both rigorous and real. That means looking beyond the obvious problem, listening carefully, and making decisions that create momentum without losing meaning.")}</p>
              <Button to="/about" variant="ghost">Our Philosophy</Button>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="perspective-media">
            <img src={IMG.executive} alt="Senior executive in a premium corporate environment" loading="lazy" />
          </Reveal>
        </div>
      </section>
      <section className="section mist">
        <div className="container">
          <SectionIntro
            eyebrow="A founder-led point of view"
            title="Meet the thinking behind the ecosystem."
            copy="Arnav Das builds at the meeting point of brand strategy, business growth, awareness and human capability."
            action={{ label: "Meet Arnav", to: "/founder" }}
          />
          <Reveal className="founder-teaser">
            <img src={IMG.founder} alt="Arnav Das, Founder of Dishan International Group" />
            <div>
              <span className="eyebrow">Arnav Das · Founder, Chairman & Managing Director</span>
              <h3>“The role of a leader is to make possibility feel practical.”</h3>
              <p>Chief Branding Officer · Strategic Brand Architect · Business Growth Consultant</p>
              <Link to="/founder" className="text-link" data-testid="founder-profile-link">Read the founder profile <ArrowRight size={15} /></Link>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <SectionIntro
            eyebrow="From the knowledge desk"
            title="Ideas for the road ahead."
            copy="Clear perspectives on strategy, brands, leadership, careers and the forces reshaping how we work."
            action={{ label: "Visit Insights", to: "/insights" }}
          />
          <div className="insight-grid">
            {ARTICLES.slice(0, 3).map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08}>
                <Link to={`/insights/${a.slug}`} className="insight-card" data-testid={`featured-insight-${i + 1}`}>
                  <div className="insight-media"><img src={a.img} alt={a.title} loading="lazy" /></div>
                  <div className="insight-body">
                    <span className="tag">{a.cat}</span>
                    <h3>{a.title}</h3>
                    <span className="insight-date">{a.date} · {a.read} <ArrowUpRight size={14} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="section testimonials">
        <div className="container">
          <SectionIntro
            eyebrow="Trusted voices"
            title="What clients and students say."
            copy="Real words from founders, executives and learners across the Dishan ecosystem."
          />
          <div className="testimonial-grid" data-testid="testimonials-section">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={(i % 3) * 0.07}>
                <figure className="testimonial-card" data-testid={`testimonial-${i + 1}`}>
                  <span className={`tag${t.tag === "Student" ? " sage" : ""}`}>{t.tag}</span>
                  <blockquote>“{t.quote}”</blockquote>
                  <figcaption>
                    <strong>{t.name}</strong>
                    <span>{t.role}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <SolutionNavigator />
      <Faq />
      <CTA />
    </main>
  );
}
